// src/shared/telemetry/types.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared type definitions for the generic telemetry dashboard factory.
// Used by DaemonOps, IoT Device Telemetry, and any future dashboard.
// ────────────────────────────────────────────────────────────────────────────

import type { TimeSeriesPoint } from '@/shared/components/plots/TelemetryTimeSeriesPlot'

/* ── Magnitude Definition — describes one telemetry metric ─────────── */

export interface MagnitudeDefinition {
  /** Unique key matching the backend metric type (e.g. 'cpu', 'temp'). */
  key: string
  /** Display title. */
  title: string
  /** Unit label for axis/stats (e.g. ' %', ' °C', ' dBm'). */
  unit: string
  /** Neon stroke colour for the cardiogram plot. */
  color: string
  /** Hard Y-axis floor (auto-scale never goes below this). */
  yFloor?: number
  /** Hard Y-axis ceiling (auto-scale never exceeds this). */
  yCeil?: number
}

/* ── Metric Group — aggregated stats for one magnitude ─────────────── */

export interface DeviceSignalStat {
  label: string
  color: string
  min: number
  max: number
  delta: number
  avg: number
}

export interface MetricGroup {
  metric: string
  unit: string
  color: string
  /** Aggregate across ALL sources. */
  min: number
  max: number
  delta: number
  avg: number
  /** Per-source breakdown (per-device in IoT, single in DaemonOps). */
  sources: DeviceSignalStat[]
}

/* ── TimeWindow preset ─────────────────────────────────────────────── */

export interface TimeWindow {
  label: string
  sec: number
  limit?: number
}

/* ── Overlay for multi-source plots ────────────────────────────────── */

export interface PlotOverlay {
  label: string
  data: TimeSeriesPoint[]
  color?: string
}

/* ── Fault labels for error banners ────────────────────────────────── */

export interface FaultInfo {
  icon: string
  title: string
  hint: string
}

export const FAULT_LABELS: Record<string, FaultInfo> = {
  network:  { icon: '🔌', title: 'Network Error',    hint: 'The BFF could not reach the orchestrator service. It may be down or unreachable.' },
  timeout:  { icon: '⏱',  title: 'Request Timeout',  hint: 'The upstream service did not respond in time. It may be overloaded or cold-starting.' },
  database: { icon: '🗄',  title: 'Database Error',   hint: 'The orchestrator encountered a database error. A migration may be pending.' },
  auth:     { icon: '🔒', title: 'Auth Error',        hint: 'Authentication or authorization failed between services.' },
  upstream: { icon: '⚠',  title: 'Upstream Error',    hint: 'The orchestrator returned an error. Check orchestrator logs for details.' },
  bff:      { icon: '🖥',  title: 'BFF Error',         hint: 'An unexpected error occurred in the BFF gateway layer.' },
}
